// Small pieces shared by the league pages.
import React from 'react';
import { Link } from 'react-router-dom';
import type { UnitType } from '../../games/capture-the-flag/types';
import { turnOffset, TRAITS } from '../../games/capture-the-flag/units';
import type { LeagueSettings } from '../../leagues/api';
import { teamName } from '../../leagues/armyBuilder';
import { CTF_ROOM_DEFAULT_CONFIG } from '../../games/capture-the-flag/room';
import type { CtfRoomConfig } from '../../games/capture-the-flag/room';
import { DEFAULT_CONFIG, EFFECT_NAMES, OUTPOST_NAMES } from '../../games/capture-the-flag/capture-the-flag';
import type { EffectId, OutpostLayout } from '../../games/capture-the-flag/types';

// The Conquer the Flag options a league can set for all its games (the rest come from the league).
export const LEAGUE_GAME_OPTIONS = ['mercyRounds', 'dominationCells', 'outposts', 'centre', 'flagWeight', 'prisons'] as const;
export function leagueGameOptions(c: CtfRoomConfig): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const k of LEAGUE_GAME_OPTIONS) { const v = c[k]; if (typeof v === 'string' || typeof v === 'number') out[k] = v; }
  return out;
}
// "Magnet center, prisons on, mercy rule after 20 rounds"
export function gameOptionsText(opts: Record<string, unknown> | undefined): string {
  const c: CtfRoomConfig = { ...CTF_ROOM_DEFAULT_CONFIG, ...(opts ?? {}) };
  const parts: string[] = [];
  if (c.outposts === 'none') parts.push('no outposts');
  else {
    const centre = c.centre && (c.centre in EFFECT_NAMES) ? `${EFFECT_NAMES[c.centre as EffectId]} center` : 'random center';
    parts.push(c.outposts === CTF_ROOM_DEFAULT_CONFIG.outposts ? centre : `${OUTPOST_NAMES[c.outposts as OutpostLayout] ?? c.outposts} outposts, ${centre}`);
  }
  parts.push(c.prisons ? 'prisons on' : 'prisons off');
  if (c.mercyRounds) parts.push(`mercy rule after ${c.mercyRounds} rounds`);
  if (c.dominationCells) parts.push(`domination at ${c.dominationCells} cells`);
  const fw = c.flagWeight ?? DEFAULT_CONFIG.carrierMovePenalty;
  if (fw !== DEFAULT_CONFIG.carrierMovePenalty) parts.push(fw ? `flag weight ${fw}` : 'no flag weight');
  return parts.join(', ');
}

const PLAYER_NAME_KEY = 'player-name';
// "3 games", "1 game".
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const storedName = () => localStorage.getItem(PLAYER_NAME_KEY) ?? '';
export const storeName = (n: string) => { try { localStorage.setItem(PLAYER_NAME_KEY, n); } catch { /* storage blocked */ } };

export function LeagueHeader({ name }: { name?: string }) {
  return (
    <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 24px', maxWidth: 1240, margin: '0 auto' }}>
      <Link to="/" className="site-brand">1001 Game Nights</Link>
      {name && <span className="site-muted" style={{ fontSize: 15 }}>Playing as <strong style={{ color: 'var(--ink)' }}>{name}</strong></span>}
    </header>
  );
}

// A unit's guard zone as a small flat-top hex grid, facing up (as in the team editor): ▲ is the unit.
export function ZoneHexes({ unit, size = 8.5 }: { unit: UnitType; size?: number }) {
  const on = new Set(unit.zone.map(o => turnOffset(o, 2).join(',')));
  const cells: React.ReactElement[] = [];
  const w = size * 2 - 1.5, h = Math.sqrt(3) * size - 1.5;
  for (let q = -3; q <= 3; q++) for (let r = -3; r <= 3; r++) {
    if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > 3) continue;
    const x = 60 + 1.5 * q * size - size, y = 56 + Math.sqrt(3) * (r + q / 2) * size - size * 0.866;
    const me = q === 0 && r === 0;
    cells.push(
      <span key={`${q},${r}`} style={{ position: 'absolute', left: x, top: y, width: w, height: h, clipPath: 'polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#24262e',
        background: me ? 'var(--ink)' : on.has(`${q},${r}`) ? 'var(--accent)' : '#3d404c' }}>{me ? '▲' : ''}</span>,
    );
  }
  return <div role="img" aria-label={`${unit.name}'s zone: ${unit.zone.length} hexes, facing up`} style={{ position: 'relative', width: 120, height: 112, flex: 'none', background: 'var(--bg)', borderRadius: 10 }}>{cells}</div>;
}

export function traitList(unit: UnitType) {
  return unit.traits.map(t => ({ name: TRAITS[t].name, text: TRAITS[t].text, good: TRAITS[t].up }));
}

// The settings, in words.
export function settingsRows(s: LeagueSettings): [string, string][] {
  const n = s.seats, per = n % 2 ? n : n - 1;
  const rounds = s.format === 'double' ? per * 2 : per;
  const games = s.format === 'double' ? n * (n - 1) : n * (n - 1) / 2;
  const each = s.format === 'double' ? 2 * (n - 1) : n - 1;
  return [
    ['Game', `Conquer the Flag · ${gameOptionsText(s.gameOptions)}`],
    ['Format', s.format === 'double' ? 'Double round robin: everyone plays everyone twice, once as Blue and once as Red' : 'Round robin: everyone plays everyone once'],
    ['Players', `${n} seats: ${rounds} rounds, ${games} games, ${each} games each${n % 2 ? '; one player sits out each round' : ''}`],
    ['Rounds', 'A round ends when all its games are played; you and your opponent pick the time'],
    ['Unplayed games', s.voidAfterDays ? `Voided ${s.voidAfterDays} days after the rest of the round is done (counts for neither player); the host can void sooner` : 'The host voids games that are holding the league up (a voided game counts for neither player)'],
    ['Armies', s.armies === 'fixed' ? `Everyone plays the same built-in team: ${teamName(s.team ?? '')}`
      : s.armies === 'pick' ? "Each player picks one of the game's built-in teams"
      : `Army builder, ${s.choices} options a pick: an anchor, a block of three core units, a striker`],
    ['Army abilities', 'Off'],
    ['Between rounds', s.improve ? 'Armies improve: after each league game (or a round you sit out) one unit gains a trait or +1 Move, your choice of two' : 'Armies stay the same'],
  ];
}

export function Seg<T extends string | number | null>({ label, value, options, onChange, note }: {
  label: string; value: T; options: [T, string, boolean?][]; onChange: (v: T) => void; note?: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="site-kicker">{label}</span>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map(([v, text, later]) => {
          const on = v === value;
          return (
            <button key={String(v)} type="button" role="radio" aria-checked={on} disabled={!!later} onClick={() => onChange(v)}
              style={{ minHeight: 40, padding: '0 14px', borderRadius: 999, fontSize: 15, cursor: later ? 'default' : 'pointer', fontFamily: 'inherit',
                ...(later ? { background: 'transparent', border: '2px dashed var(--panel-line)', color: 'var(--faint)' }
                  : on ? { background: 'var(--accent-bg)', border: '2px solid var(--accent)', color: 'var(--ink)', fontWeight: 600 }
                  : { background: 'var(--panel)', border: '2px solid var(--panel-line)', color: '#d6d4df' }) }}>
              {text}{later ? ' · later' : ''}
            </button>
          );
        })}
      </div>
      {note && <p className="site-muted" style={{ fontSize: 14 }}>{note}</p>}
    </div>
  );
}

export function MemoryNote() {
  return (
    <p role="note" style={{ padding: '10px 14px', borderRadius: 10, background: '#4a3f22', color: '#f2d48f', fontSize: 14 }}>
      This server isn't saving leagues yet: a restart or an update to the site loses them. Fine for trying it out.
    </p>
  );
}
